import { useCallback, useState } from "react";

import styles from "../styles/CopyButton.module.css";

export interface CopyButtonProps {
  readonly value: unknown;
  readonly label?: string;
}

const RESET_DELAY_MS = 1200;

export function CopyButton({
  value,
  label = "Copy",
}: CopyButtonProps): React.JSX.Element {
  const [state, setState] = useState<"idle" | "copied" | "error">("idle");

  const onClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      // The button lives inside <details><summary>, where a click would
      // otherwise toggle the disclosure. We swallow that interaction so
      // copying doesn't collapse the panel under the user's cursor.
      event.preventDefault();
      event.stopPropagation();
      try {
        const text =
          typeof value === "string" ? value : JSON.stringify(value, null, 2);
        await navigator.clipboard.writeText(text);
        setState("copied");
      } catch {
        setState("error");
      } finally {
        window.setTimeout(() => setState("idle"), RESET_DELAY_MS);
      }
    },
    [value],
  );

  const text =
    state === "copied" ? "Copied ✓" : state === "error" ? "Failed" : label;

  return (
    <button
      type="button"
      className={styles.btn}
      data-state={state}
      onClick={onClick}
    >
      {text}
    </button>
  );
}
